// Recibos por medidor: tomar foto (cámara del teléfono) o subir PDF/imagen, varios a la vez; contador "3 de 12";
// estado de cada archivo según la última lectura; botón "Leer recibos" (etapa 4: los PDF digitales de CFE se leen SIN
// IA, gratis; fotos y escaneos con la IA o, sin llave, a mano). También fotos del sitio y otros archivos.
// (Desde la ADENDA A0 ya no se sube la cotización de FINSUS: la mensualidad es de referencia y la calcula el motor.)
import { useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { Camera, ScanText, Trash2, Upload } from 'lucide-react';
import { BYTES_MAXIMOS_POR_ARCHIVO, MENSAJE_RECORTE_PIDE_12, MODELOS_IA, TIPOS_ARCHIVO_ACEPTADOS, fechaHoraCorta, recibosEsperados, recibosQueCuentan } from '@cotizador/compartido';
import type { ArchivoSubido, Medidor } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { ErrorDeApi } from '../../api/cliente.ts';
import { navegar } from '../../ruteo.tsx';
import { usarUsuario } from '../../sesion.tsx';
import { Boton, BotonConfirmar, CampoSelect, Chip, ErrorEnLinea, Seccion, Vacio } from '../../componentes/ui.tsx';
import { IconoArchivo } from '../../componentes/archivos.tsx';
import { bytes, plural } from '../../logica/etiquetas.ts';
import { lecturaConDatos } from '../../logica/lectura.ts';
import { estadoDeArchivo, recibosSinLeer, textoEstado } from '../../logica/archivos-leidos.ts';
import { prepararFoto } from '../../logica/reducir-foto.ts';
import { usarCotizacion } from './contexto.tsx';
import { CartelFacturacion } from './CartelFacturacion.tsx';

type Clase = 'recibo' | 'foto_sitio' | 'otro';
const ETIQUETA_CLASE: Record<ArchivoSubido['clase'], string> = {
  recibo: 'Recibo',
  // Legado (antes de la ADENDA A0): se sigue enseñando si un borrador viejo lo trae, pero ya no se ofrece subirlo.
  finsus: 'Cotización FINSUS (anterior)',
  foto_sitio: 'Foto del sitio',
  otro: 'Otro',
  pdf_propuesta: 'PDF de la propuesta',
  portada_jpg: 'Portada',
  avisos_txt: 'Avisos',
  json_v1: 'Datos del PDF',
  falla_html: 'Falla del PDF',
};

/** Revisa el TIPO (y que no esté vacío) antes de subir: el servidor también lo revisa, pero así se avisa sin esperar. */
export function problemaDeTipo(f: File): string | null {
  const tipo = f.type || (f.name.toLowerCase().endsWith('.heic') ? 'image/heic' : '');
  if (!(TIPOS_ARCHIVO_ACEPTADOS as readonly string[]).includes(tipo)) return `"${f.name}" no es PDF ni foto (JPG, PNG, WebP o HEIC).`;
  if (f.size === 0) return `"${f.name}" está vacío.`;
  return null;
}

/** Revisa tipo y tamaño ANTES de subir (el tamaño, ya con la foto reducida): el servidor también lo revisa. */
export function problemaDeArchivo(f: File): string | null {
  const tipo = problemaDeTipo(f);
  if (tipo) return tipo;
  if (f.size > BYTES_MAXIMOS_POR_ARCHIVO) return `"${f.name}" pesa ${bytes(f.size)}; el máximo es 20 MB.`;
  return null;
}

/** Errores de UN archivo (repetido, tipo, peso, tope de recibos del medidor): se dice cuál y se sigue con los demás. Lo
 *  demás (sin red, sin sesión, la cotización cambió de verdad…) detiene la subida y dice qué no se subió. */
const PROBLEMAS_DE_UN_ARCHIVO: ReadonlySet<string> = new Set(['DATOS_INVALIDOS', 'ARCHIVO_NO_ACEPTADO', 'LIMITE']);

function Subidor({ medidor, clase, etiquetaCamara, deshabilitado }: { medidor: Medidor; clase: Clase; etiquetaCamara: string; deshabilitado?: boolean }) {
  const { ejecutar, lectura } = usarCotizacion();
  const camara = useRef<HTMLInputElement>(null);
  const archivo = useRef<HTMLInputElement>(null);
  const [progreso, setProgreso] = useState<{ hecho: number; total: number; esperando?: boolean } | null>(null);
  const [problemas, setProblemas] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [encima, setEncima] = useState(false);

  async function subir(lista: File[]) {
    setError(null);
    // El tipo se revisa antes; el PESO, después de reducir la foto (una de 25 MB del celular cabe ya reducida).
    const malos = lista.map(problemaDeTipo).filter((x): x is string => x !== null);
    setProblemas(malos);
    const buenos = lista.filter((f) => problemaDeTipo(f) === null);
    if (!buenos.length) return;
    // Si se está leyendo, estos se suben al terminar la lectura (subir a media lectura la hacía terminar en 409).
    setProgreso({ hecho: 0, total: buenos.length, esperando: lectura.leyendo });
    let subioRecibo = false;
    // Verificación adversarial de 9e40380: un archivo repetido a media ráfaga cortaba la subida (los que seguían no se
    // subían) y la lectura automática saltaba a Revisión, así que el error desaparecía. Ahora un problema de UN archivo
    // se dice con su nombre y se sigue con los demás, y con cualquier problema la pantalla se queda en Recibos.
    let conProblemas = malos.length > 0;
    try {
      await lectura.empezarSubida();
      setProgreso({ hecho: 0, total: buenos.length });
      for (let i = 0; i < buenos.length; i++) {
        // Nube, tanda 1: la foto se reduce AQUÍ (menos datos del celular; en Linux no hay sips). Si el navegador no puede
        // abrirla, sube la original y el servidor la convierte o avisa (logica/reducir-foto.ts).
        const { archivo: f } = await prepararFoto(buenos[i]!);
        const pesada = problemaDeArchivo(f);
        if (pesada) {
          conProblemas = true;
          setProblemas((p) => [...p, pesada]);
          setProgreso({ hecho: i + 1, total: buenos.length });
          continue;
        }
        const conTipo = f.type ? f : new File([f], f.name, { type: 'image/heic' });
        try {
          // Subir no pisa nada: si la cotización cambió mientras tanto, se recarga y se sube otra vez sola.
          await ejecutar((c) => api.subirArchivo(c.id, c.revision, conTipo, medidor, clase), { reintentar: true });
          if (clase === 'recibo') subioRecibo = true;
        } catch (e) {
          if (!(e instanceof ErrorDeApi && PROBLEMAS_DE_UN_ARCHIVO.has(e.codigo))) {
            const faltan = buenos.slice(i).map((x) => `«${x.name}»`);
            setProblemas((p) => [...p, `No se ${faltan.length === 1 ? 'subió' : 'subieron'} ${faltan.join(', ')}: vuelve a elegir${faltan.length === 1 ? 'lo' : 'los'}.`]);
            throw e;
          }
          conProblemas = true;
          setProblemas((p) => [...p, `«${buenos[i]!.name}» no se subió: ${e.message}`]);
        }
        setProgreso({ hecho: i + 1, total: buenos.length });
      }
    } catch (e) {
      conProblemas = true;
      setError(e);
    } finally {
      setProgreso(null);
      // Al terminar de subir recibos, la lectura arranca sola (contexto.tsx); con problemas, sin salir de Recibos.
      lectura.terminarSubida(subioRecibo, conProblemas);
    }
  }

  const alElegir = (e: ChangeEvent<HTMLInputElement>) => {
    const lista = [...(e.target.files ?? [])];
    e.target.value = '';
    void subir(lista);
  };
  const alSoltar = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setEncima(false);
    if (deshabilitado) return;
    void subir([...e.dataTransfer.files]);
  };

  return (
    <div
      className={['subidor', encima ? 'es-encima' : ''].join(' ')}
      onDragOver={(e) => {
        e.preventDefault();
        setEncima(true);
      }}
      onDragLeave={() => setEncima(false)}
      onDrop={alSoltar}
    >
      <input ref={camara} type="file" accept="image/*" capture="environment" multiple hidden onChange={alElegir} />
      <input ref={archivo} type="file" accept="application/pdf,image/*,.heic,.heif" multiple hidden onChange={alElegir} />
      <div className="subidor__botones">
        <Boton icono={<Camera aria-hidden />} disabled={deshabilitado || progreso !== null} onClick={() => camara.current?.click()} className="solo-celular-flex">
          {etiquetaCamara}
        </Boton>
        <Boton icono={<Upload aria-hidden />} disabled={deshabilitado || progreso !== null} cargando={progreso !== null} onClick={() => archivo.current?.click()}>
          {progreso ? (progreso.esperando ? 'Se suben al terminar la lectura…' : `Subiendo ${Math.min(progreso.hecho + 1, progreso.total)} de ${progreso.total}…`) : 'Subir PDF o imagen'}
        </Boton>
        <span className="subidor__soltar solo-escritorio">o suelta aquí los archivos</span>
      </div>
      {problemas.length > 0 && (
        <ul className="subidor__problemas" role="alert">
          {problemas.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </div>
  );
}

function ListaArchivos({ archivos }: { archivos: ArchivoSubido[] }) {
  const { ejecutar, cot } = usarCotizacion();
  const { puedeEn } = usarUsuario();
  const [quitando, setQuitando] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const quitar = async (a: ArchivoSubido) => {
    setQuitando(a.id);
    setError(null);
    try {
      await ejecutar((c) => api.quitarArchivo(c.id, a.id, c.revision), { reintentar: true });
    } catch (e) {
      setError(e);
    } finally {
      setQuitando(null);
    }
  };
  if (!archivos.length) return null;
  const b = cot.borrador;
  // "Repetido del recibo 2": el número es el lugar del archivo en la lista de su medidor (como las pastillas del cotejo).
  const numeroDe = (id: string) => {
    const x = b.archivos.find((y) => y.id === id);
    if (!x) return null;
    const i = b.archivos.filter((y) => y.clase === 'recibo' && !y.eliminado && y.medidor === x.medidor).findIndex((y) => y.id === id);
    return i < 0 ? null : i + 1;
  };
  return (
    <>
      <ul className="archivos">
        {archivos.map((a) => {
          const estado = estadoDeArchivo(b, a);
          const t = textoEstado(estado, numeroDe);
          const nota = estado.tipo === 'leido' && estado.leido.estado !== 'leido' ? estado.leido.nota : null;
          return (
            <li key={a.id} className="archivo">
              <span className="archivo__icono">
                <IconoArchivo archivo={a} />
              </span>
              <span className="archivo__datos">
                <span className="archivo__nombre">{a.nombre_original}</span>
                <span className="archivo__meta">
                  {ETIQUETA_CLASE[a.clase]} · {bytes(a.bytes)} · {fechaHoraCorta(a.subido_en)}
                </span>
                {(t || nota) && (
                  <span className="archivo__estado">
                    {t && <Chip tono={t.tono}>{t.texto}</Chip>}
                    {nota && <span className="archivo__nota">{nota}</span>}
                  </span>
                )}
              </span>
              {puedeEn('editar_cotizaciones', cot) && (
                <BotonConfirmar
                  pregunta="¿Quitar?"
                  cargando={quitando === a.id}
                  icono={<Trash2 aria-hidden />}
                  etiquetaAccesible={`Quitar ${a.nombre_original}`}
                  alConfirmar={() => void quitar(a)}
                />
              )}
            </li>
          );
        })}
      </ul>
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </>
  );
}

/** `subidos` = los que CUENTAN (sin los que la última lectura dejó fuera del año); `fuera` = cuántos no cuentan. */
function Contador({ subidos, esperados, fuera = 0, obligatorios = false }: { subidos: number; esperados: number; fuera?: number; obligatorios?: boolean }) {
  if (esperados <= 1)
    return <p className="contador">{subidos ? plural(subidos, 'recibo subido', 'recibos subidos') : 'Sube el último recibo: trae el historial del año.'}</p>;
  return (
    <div className="contador">
      <p>
        <strong>
          {subidos} de {esperados}
        </strong>{' '}
        recibos {subidos < esperados ? (obligatorios ? '· el recorte de punta pide los 12: con menos no se calcula' : '· no es obligatorio: los meses que falten se estiman') : '· completos'}
        {fuera > 0 && ` · ${fuera === 1 ? '1 recibo más viejo que el año que se cotiza no cuenta' : `${fuera} recibos más viejos que el año que se cotiza no cuentan`}`}
      </p>
      <div className="contador__barra" role="progressbar" aria-valuemin={0} aria-valuemax={esperados} aria-valuenow={Math.min(subidos, esperados)} aria-label="Recibos subidos">
        {Array.from({ length: esperados }, (_, i) => (
          <span key={i} className={i < subidos ? 'es-lleno' : ''} />
        ))}
      </div>
    </div>
  );
}

export function SeccionRecibos() {
  const { cot, lectura } = usarCotizacion();
  const { config, puedeEn } = usarUsuario();
  const [claseOtro, setClaseOtro] = useState<Clase>('foto_sitio');
  const b = cot.borrador;
  const q = b.cuestionario;
  const medidores: Medidor[] = q.dos_medidores ? ['A', 'B'] : ['A'];
  const vivos = b.archivos.filter((a) => !a.eliminado);
  const recibosDe = (m: Medidor) => vivos.filter((a) => a.clase === 'recibo' && a.medidor === m);
  const hayRecibos = vivos.some((a) => a.clase === 'recibo');
  const ia = config.ia;

  const sinLeer = recibosSinLeer(b).length;

  return (
    <div className="pila pila--grande">
      {q.consumo.es_nuevo ? (
        <Vacio titulo="Cliente nuevo: no hacen falta recibos">
          El consumo se estima con lo que contestaste en el cuestionario. Aquí puedes subir fotos del sitio u otros archivos que ayuden.
        </Vacio>
      ) : (
        medidores.map((m) => {
          const esperados = recibosEsperados(q, b.lecturas[m]?.tarifa ?? null);
          const lista = recibosDe(m);
          const cuenta = recibosQueCuentan(lista, b.archivos_leidos);
          return (
            <Seccion
              key={m}
              id={`recibos-${m}`}
              titulo={q.dos_medidores ? `Recibos del medidor ${m}` : 'Recibos de CFE'}
              descripcion={q.recorte_punta ? MENSAJE_RECORTE_PIDE_12 : esperados === 12 ? 'GDMTH: sube los recibos que tenga (lo ideal, los 12). Con los que haya se cotiza; los meses que falten se estiman.' : 'El último recibo trae el historial del año. Si tienes más, súbelos también.'}
            >
              <div className="hoja pila">
                <Contador subidos={cuenta.cuentan} fuera={cuenta.fuera} esperados={esperados} obligatorios={!!q.recorte_punta} />
                {lecturaConDatos(b.lecturas[m]) && <CartelFacturacion facturacion={b.lecturas[m]!.facturacion ?? b.resultado?.facturacion ?? null} leida={!!b.lecturas[m]!.facturacion} lectura={b.lecturas[m]!} enRecibos />}
                <ListaArchivos archivos={lista} />
                <Subidor medidor={m} clase="recibo" etiquetaCamara="Tomar foto del recibo" deshabilitado={!puedeEn('capturar_costos', cot)} />
              </div>
            </Seccion>
          );
        })
      )}

      {!q.consumo.es_nuevo && puedeEn('editar_cotizaciones', cot) && (
        <div className="leer-ia">
          <div className="leer-ia__texto">
            <p className="leer-ia__titulo">Leer los recibos</p>
            <p className="leer-ia__detalle">
              Los recibos en PDF de CFE se leen sin IA: gratis y en segundos.{' '}
              {ia.modo === 'sin_llave'
                ? 'Las fotos y los escaneos se capturan a mano en Revisión (la IA todavía no tiene llave).'
                : ia.modo === 'simulado'
                  ? 'Las fotos y los escaneos, con la IA simulada (respuestas de prueba).'
                  : `Las fotos y los escaneos los lee ${MODELOS_IA[ia.modelo]?.nombre ?? ia.modelo} con tu cuestionario y tus notas.`}{' '}
              Al terminar de subir recibos se leen solos; después revisas la tabla y la autorizas.
            </p>
            {lectura.leyendo && (
              <p className="leer-ia__detalle" role="status">
                {lectura.esperandoOtra
                  ? 'Ya se está leyendo esta cotización en otra pestaña u otro teléfono (o sacando su PDF): en cuanto termine, se leen los recibos que faltan.'
                  : 'Leyendo los recibos… Si subes más, se suben al terminar y se leen después.'}
              </p>
            )}
          </div>
          <div className="leer-ia__botones">
            <Boton variante="principal" icono={<ScanText aria-hidden />} cargando={lectura.leyendo} disabled={!hayRecibos || lectura.subiendo} onClick={() => void lectura.leer()}>
              {lectura.leyendo ? 'Leyendo…' : lectura.subiendo ? 'Se leen al terminar de subir' : lecturaConDatos(b.lecturas.A) && !sinLeer ? 'Volver a leer' : 'Leer recibos'}
            </Boton>
            {ia.modo === 'sin_llave' && (
              <Boton variante="fantasma" onClick={() => navegar(`/cotizaciones/${cot.id}/revision`)}>
                Capturar a mano
              </Boton>
            )}
          </div>
        </div>
      )}
      <ErrorEnLinea error={lectura.error} alCerrar={lectura.limpiarError} />

      <Seccion id="otros-archivos" titulo="Otros archivos" descripcion="Fotos del techo y la mufa, o lo que ayude.">
        <div className="hoja pila">
          <ListaArchivos archivos={vivos.filter((a) => a.clase !== 'recibo')} />
          <CampoSelect
            etiqueta="¿Qué vas a subir?"
            value={claseOtro}
            onChange={(e) => setClaseOtro(e.target.value as Clase)}
            opciones={[
              { valor: 'foto_sitio', texto: 'Foto del sitio' },
              { valor: 'otro', texto: 'Otro' },
            ]}
          />
          <Subidor medidor="A" clase={claseOtro} etiquetaCamara="Tomar foto" deshabilitado={!puedeEn('capturar_costos', cot)} />
        </div>
      </Seccion>
    </div>
  );
}
